import CollectionPlayer from "@/employee/CollectionPlayer"
import util from "@/util"
import ValueRange, { MapStringAny } from "@/effects/ValueRange"
import { reactive } from "vue"
import Research from "@/Research"
import {
  custom,
  deserialize,
  identifier,
  list,
  map,
  object,
  primitive,
  raw,
  reference,
  serializable, serialize,
  SKIP
} from "serializr"
import ValueMod from "@/effects/ValueMod"
import EffectManager from "@/effects/EffectManager"
import Farm from "@/farm/Farm"
import Laboratory from "@/farm/Laboratory"
import globalState from "@/store/globalState"
import EffectType from "@/effects/EffectType"
import ChatHistory from "@/employee/ChatHistory"
import {
  Personality,
} from "@/employee/Personality"
import { Inventory } from "@/employee/Inventory"
import { getPersonality, randomPersonality } from "@/employee/Personalities"
import FarmEvent from "@/FarmEvent"

export default class Employee {
  private farm: Farm = new Farm()

  @serializable(custom(v => {}, (json, context) => new CollectionPlayer("liliana", context.json.frameInfo)))
  private player: CollectionPlayer = new CollectionPlayer()

  @serializable(raw())
  private frameInfo: any = {}

  @serializable(identifier(primitive))
  public readonly uuid: string = util.uuid()

  @serializable(list(primitive()))
  public names: string[] = []

  // Stats
  @serializable(object(ValueRange))
  public iq: ValueRange = new ValueRange(80 + Math.random() * 40, 20, 180,
    ["Brainfucked", "Fuckdoll", "Brainless", "Airhead", "Ditzy", "Below Average", "Average", "Bright", "Astute", "Smart", "Brilliant", "Genius"]
  )

  @serializable(object(ValueRange))
  public milk: ValueRange = new ValueRange(0, 0, 0.5, [], true)

  @serializable(object(ValueRange))
  public milkDelta: ValueRange = new ValueRange(Math.max(0.01 * Math.random() - 0.002, 0), 0, 1000)

  @serializable(object(ValueRange))
  public milkingSkill: ValueRange = new ValueRange(1, 0, 100)

  @serializable(object(ValueRange))
  public lust: ValueRange = new ValueRange(0, 0, 100,
    ["Satisfied", "Not quite satisfied", "Slightly horny", "Turned on", "Horny", "Horny as fuck", "Begging to be fucked"],
    true
  )

  @serializable(object(ValueRange))
  public lustDelta: ValueRange = new ValueRange(0.1, 0, 1000)

  @serializable(object(ValueRange))
  public mood: ValueRange = new ValueRange(50, 0, 100, ["Depressed", "Sad", "Moody", "Content", "Happy", "Joyful", "Ecstatic"])

  // Other properties
  @serializable
  public assignment: string = 'farm' // office, farm, lab

  @serializable
  public officeTask: string = 'milking' // milking, sales, budget

  @serializable
  public isBeingMilked: boolean = false

  @serializable(object(Inventory))
  public inventory: Inventory

  @serializable(object(ValueRange))
  public salary: ValueRange = new ValueRange(this.targetSalary, 0, undefined, [], true)

  @serializable(object(ValueRange))
  private implants: ValueRange = new ValueRange(0, 0, 10)

  @serializable
  private lastCuddleTime: number = 0

  @serializable(object(ValueRange))
  private timeUnhappy: ValueRange = new ValueRange(0, 0, 180, [], true)

  @serializable(list(primitive()))
  public _personalities: string[] = []

  @serializable
  private _money: number = 0

  @serializable(object(EffectManager))
  effectManager: EffectManager = new EffectManager()

  @serializable(object(ChatHistory))
  chatHistory: ChatHistory = new ChatHistory()

  get effectToModsMap() {
    return {
      "iq": this.iq.valueMods,
      "milkStorage": this.milk.maxMods,
      "milkingSkill": this.milkingSkill.valueMods,
      "production": this.milkDelta.valueMods,
      "mood": this.mood.valueMods,
      "lust": this.lust.valueMods,
      "lustDelta": this.lustDelta.valueMods,
      "implants": this.implants.valueMods
    }
  }

  get _constants(): { [key: string]: ValueRange } {
    return {
      "cuddleDelay": new ValueRange(20, 0),
      "cuddleReqLust": new ValueRange(0, 0),
      "cuddleDuration": new ValueRange(60, 0),
      "cuddleLust": new ValueRange(10, -100, 100),
      "sexReqLust": new ValueRange(50, 0),
      "sexMood": new ValueRange(25, 0),
      "sexMoodDuration": new ValueRange(120, 0),
      "sexLustLoss": new ValueRange(60, 0, 100),
      "masturbationLustLoss": new ValueRange(80, 0, 100),
      "masturbationMoodLoss": new ValueRange(5, 0, 100),
      "giftMood": new ValueRange(30, 0, 100),
      "salaryMood": new ValueRange(50, 0, 100),
      "fullMilkMoodFactor": new ValueRange(0.2, 0, 1),
      "heatLustMul": new ValueRange(3, 0, 10),
      "heatTimeMul": new ValueRange(1, 0, 5),
      "researchMul": new ValueRange(2.3, 0, 100)
    }
  }

  private static possibleSequences: any[] = []

  constructor(farm: Farm) {
    this.farm = farm
    this.inventory = new Inventory(this.farm, this)

    let firstnames = require("@/assets/names.json").firstnames
    this.names = util.sample(firstnames)

    // Choose our image sequence.
    // We don't choose completely randomly, but make sure that every possible choice is used before we may select same again.
    if (Employee.possibleSequences.length == 0) Employee.possibleSequences = JSON.parse(globalState().characterSequences.value)
    let frameInfos = Employee.possibleSequences
    let sequenceIndex = Math.floor(frameInfos.length * Math.random())
    let frameInfo: any = frameInfos.splice(sequenceIndex, 1)[0]
    if (frameInfo != undefined) {
      this.frameInfo = frameInfo
      this.player = new CollectionPlayer(frameInfo.id, this.frameInfo)
    }

    this._personalities.push(randomPersonality("prefix", this._personalities).id)
    this._personalities.push(randomPersonality("suffix", this._personalities).id)
  }

  load(state: any) {
    if (state.frameInfo.id != undefined) {
      let frameInfos: any[] = JSON.parse(globalState().characterSequences.value)
      // If the id exists, load that frameinfo
      let frameInfoIndex = frameInfos.findIndex((info: any) => { return info.id == state.frameInfo.id })
      if (frameInfoIndex != -1) {
        this.frameInfo = frameInfos[frameInfoIndex]
        this.player = new CollectionPlayer(this.frameInfo.id, this.frameInfo)
      }
    }

    this.names = state.names
    this.iq.load(state.iq)
    this.milk.load(state.milk)
    this.milkDelta.load(state.milkDelta)
    this.milkingSkill.load(state.milkingSkill)
    this.mood.load(state.mood)
    this.lust.load(state.lust)
    this.lustDelta.load(state.lustDelta, false)

    this.assignment = state.assignment
    this.officeTask = util.ensure(state.officeTask, ["milking", "sales", "budget"])
    this.salary.load(state.salary)
    this._money = state._money
    this.isBeingMilked = state.isBeingMilked
    this._personalities = state._personalities
    this.lastCuddleTime = state.lastCuddleTime
    this.timeUnhappy.load(state.timeUnhappy)
    this.implants = state.implants

    this.inventory.load(state.inventory)

    this.effectManager = deserialize(EffectManager, state.effectManager)
    this.effectManager.refreshEffects(this.farm.state.lab as Laboratory, this.effectToModsMap)

    this.chatHistory = deserialize(ChatHistory, state.chatHistory)
  }

  update(dt: number, farm: Farm) {
    this.farm = farm

    this.inventory.update(dt)

    // Set assignment modifier
    if (this.assignment === 'farm') {
      this.milkDelta.valueMods.assignmentAdd = new ValueMod(0.001, 'add')
      this.milkDelta.valueMods.assignmentMul = new ValueMod(1, 'mul')
    } else {
      this.milkDelta.valueMods.assignmentAdd = new ValueMod(0, 'add')
      this.milkDelta.valueMods.assignmentMul = new ValueMod(0.5, 'mul')
    }

    // Milk modifiers to stats
    this.milk.baseValue += this.milkDelta.value * dt
    if (this.milk.max > 0.2 && this.milk.fraction >= 1) {
      this.effectManager.addTempEffect(
        'needMilking',
        'mood',
        new ValueMod(this.constants['fullMilkMoodFactor'], 'mul'),
        new EffectType(60, 'slow', 'temporary')
      )
      this.chatHistory.addMessage(this.getSpeech("needMilking"), { id: "needMilking", maxFrequency: 30 })
    } else {
      this.effectManager.removeTempEffect('needMilking', 'mood')
    }

    // Lust modifiers to stats
    this.lust.baseValue += this.lustDelta.value * dt
    if (this.lust.fraction == 1) {
      this.masturbate()
    }
    this.iq.valueMods.lust = new ValueMod(0.85 + 0.15 * (1 - this.lust.fraction), 'mul')
    this.milkDelta.valueMods.lust = new ValueMod(1 + 1 * this.lust.fraction, 'mul')

    // Mood modifiers to stats
    this.milkDelta.valueMods.mood = new ValueMod(0.75 + this.mood.fraction * 0.5, 'mul')
    //this.milkDelta.valueMods.mood = new ValueMod(1, 'mul')

    // Quit if employee was too unhappy for too long
    if (this.mood.fraction < 0.2) {
      this.timeUnhappy.addValue(dt)

      if (this.timeUnhappy.fraction > 0.2) {
        this.chatHistory.addMessage(
          `I don't like it here. If nothing improves, I'll quit in ${util.displayTime(this.timeUnhappy.max - this.timeUnhappy.value)}.`,
          { id: "quittingSoon", maxFrequency: 30 }
        )
      }
    } else {
      this.timeUnhappy.addValue(-dt)
    }
    if (this.timeUnhappy.fraction >= 1) this.quit()

    // personalities
    for (let p of this.personalities) {
      p.onUpdate(dt, this)
    }

    this.effectManager.update(dt, this.effectToModsMap)
    this.chatHistory.update(dt, this.farm)

    this.updateOfficeTasks(dt)
  }

  updateOfficeTasks(dt: number) {
    if (this.assignment != 'office') {
      return
    }

    if (this.officeTask == 'milking') {
    } else if (this.officeTask == 'sales') {
    } else if (this.officeTask == 'budget') {
    }
  }

  gift() {
    this.effectManager.addTempEffect(
      'gift',
      'mood',
      new ValueMod(this.constants["giftMood"], 'add'),
      new EffectType(60, 'instant', 'temporary')
    )
    this.chatHistory.addMessage(this.getSpeech('gift'))

    for (let p of this.personalities) {
      p.onGift(this)
    }
  }

  cuddle() {
    if (!this.canCuddle) return

    this.effectManager.addTempEffect(
      'cuddle',
      'mood',
      new ValueMod(10, 'add'),
      new EffectType(this.constants["cuddleDuration"], 'instant', 'temporary')
    )
    this.lust.addValue(this.constants["cuddleLust"])

    this.chatHistory.addMessage(this.getSpeech('cuddle'), { id: 'cuddle', maxFrequency: 20 })

    for (let p of this.personalities) {
      p.onCuddle(this)
    }

    this.lastCuddleTime = this.farm.state.time
  }

  fuck() {
    if (!this.canFuck) return

    let lustChange = this.lust.addValue(-this.constants["sexLustLoss"])
    this.effectManager.addTempEffect(
      `hadSex-${util.uuid()}`, // Effect stacks, so using uuid
      'mood',
      new ValueMod(this.constants["sexMood"], 'add'),
      new EffectType(this.constants["sexMoodDuration"], 'instant', 'temporary')
    )
    for (let p of this.personalities) {
      p.onSex(this)
    }
    this.chatHistory.addMessage(this.getSpeech('hadSex'))
  }

  masturbate() {
    this.lust.addValue(-this.constants["masturbationLustLoss"])

    // Add a long term mood loss effect. This effect can stack infinitely, so each has its own id.
    this.effectManager.addTempEffect(
      `hadMasturbation-${util.uuid()}`,
      'mood',
      new ValueMod(-this.constants["masturbationMoodLoss"], 'add'),
      new EffectType(240, 'instant', 'temporary')
    )

    this.chatHistory.addMessage(this.getSpeech('masturbation'))
  }

  get canFuck(): boolean {
    return this.lust.value >= this.constants["sexReqLust"]
  }

  get canCuddle(): boolean {
    return (this.lastCuddleTime + this.constants["cuddleDelay"] < this.farm.state.time || this.lastCuddleTime == 0) &&
      this.lust.value > this.constants["cuddleReqLust"]
  }

  get targetSalary(): number {
    let iq = Math.pow(this.iq.fraction, 2)
    let salary = 0.1 + iq
    //console.log(salary * 60)
    return salary
    //return (this.iq.baseValue / this.iq.max) * 0.5 + this.milkDelta.baseValue * 0.5
  }

  get bimbofactor(): number {
    let iq = (1 - this.iq.fraction)
    //let milkmax = util.clamp(this.milk.max, 0, 8) / 10
    let implants = this.implants.value
    let implantsFactor = util.clamp(implants / 8, 0, 1)
    let min = 0
    //if (milkmax > 0.4) min = 0.5 + (milkmax - 0.4) * 0.5
    if (implants >= 2) min = util.clamp(min, 0.5, 1)
    if (implants >= 5) min = util.clamp(min, 0.6, 1)
    if (implants >= 8) min = util.clamp(min, 0.7, 1)

    return util.clamp(util.clamp(0.75 * iq + 0.4 * implantsFactor, min, 1), 0, 1)
  }

  get image(): string {
    let maxIndex = this.player.activeSequence().getLength() - 1
    let index = util.clamp(Math.round(this.bimbofactor * maxIndex), 0, maxIndex)
    //let index = Math.floor(this.bimbofactor * this.player.activeSequence().getLastIndex())
    this.player.goto(index)
    return this.player.get()
  }

  /** How fast this employee can milk cows if assigned to milking duty */
  get milkingSpeed(): number {
    return 0.2 * this.mood.fraction * this.milkingSkill.value * (1 + this.iq.fraction)
  }

  /** How much this employee increases milk price if assigned to sales duty */
  get milkConsumptionIncrease(): ValueMod {
    let implants = this.implants.value
    let breastSize = 0
    let breastAttractiveness = 1

    while (implants > 0) {
      breastSize += breastAttractiveness
      breastAttractiveness *= 0.8
      implants -= 1
    }

    let base = this.iq.fraction + util.clamp(breastSize / 4, 0, 1)
    return new ValueMod(1 + base * this.mood.fraction, 'mul')
  }

  pay(amount: number) {
    this._money += amount
  }

  get money(): number {
    return this._money
  }

  get name(): string {
    return util.pickFrom(this.names, 1 - this.iq.fraction)
  }

  get moodIqModifier() {
    return 0.5 + 0.5 * this.mood.fraction
  }

  private constantsBuffer: { [key: string]: number } = {}
  private constantsLastUpdate: number = 0
  get constants(): { [key: string]: number } {
    // If constants were updated recently, just return the buffer
    if (this.farm.state.time - this.constantsLastUpdate < 2 && this.constantsLastUpdate > 0) return this.constantsBuffer

    let cs: { [key: string]: ValueRange } = {}
    // For each constant
    for (let [constant, constantRange] of Object.entries(this._constants)) {
      cs[constant] = constantRange.copy

      // Get all mods to this constant
      let mods: ValueMod[] = []
      for (let p of this.personalities) {
        mods = mods.concat((p.constantMods[constant] == undefined) ? [] : p.constantMods[constant])
      }

      // Add all mods to the constant
      for (let mod of mods) {
        cs[constant].valueMods[mod.uuid] = mod
      }
    }

    // Convert all constants to just numbers
    let cs2: { [key: string]: number } = {}
    for (let [key, range] of Object.entries(cs)) {
      cs2[key] = range.value
    }

    this.constantsLastUpdate = this.farm.state.time
    this.constantsBuffer = cs2
    return cs2
  }

  private personalitiesBuffer: Personality[] = []
  private personalitiesLastUpdate: number = 0
  get personalities(): Personality[] {
    // If updated recently, just return the buffer
    if (this.farm.state.time - this.personalitiesLastUpdate < 1 && this.personalitiesLastUpdate > 0) return this.personalitiesBuffer

    let list = this._personalities.map(p => getPersonality(p))
    // Sort alphabetically on personality name
    list.sort((a, b) => {
      if (a.id < b.id) return -1
      if (a.id > b.id) return 1
      return 0
    })
    // Sort by prefix/suffix
    list.sort((a, b) => {
      let aNr = (a.affix == 'prefix') ? 0 : 1
      let bNr = (b.affix == 'prefix') ? 0 : 1
      return aNr - bNr
    })

    this.personalitiesLastUpdate = this.farm.state.time
    this.personalitiesBuffer = list
    return list
  }

  say(type: string, id: string = util.uuid(), maxFrequency: number = 10) {
    this.chatHistory.addMessage(this.getSpeech(type), { id: id, maxFrequency: maxFrequency })
  }

  private _dialogues: any = undefined
  getSpeech(type: string): string {
    if (this._dialogues == undefined) this._dialogues = JSON.parse(globalState().dialogueString.value)
    let def = this._dialogues.default
    if (type in def) return util.pickFrom(def[type], 1 - this.iq.fraction)
    return `Error. No dialogue for "${type}"`
  }

  /** This employee stops working at the farm. */
  quit() {
    let staff = this.farm.state.employees as Employee[]
    let index = staff.findIndex((employee) => employee.uuid == this.uuid)
    staff.splice(index, 1)

    this.farm.state.eventManager.addEvent(new FarmEvent(() => {
      return `${this.name} has quit.`
    }), this.farm)
  }

  assignmentTitle(): string {
    if (this.assignment == 'office') {
      if (this.officeTask == 'milking') return 'Milkmaid'
      else if (this.officeTask == 'sales') return 'Secretary'
      return 'Secretary'
    } else if (this.assignment == 'farm') {
      return 'Cowgirl'
    } else if (this.assignment == 'lab') {
      return 'Scientist'
    }
    return ''
  }

  static totalIQ(employees: Employee[]): ValueRange {
    if (employees.length == 0) return new ValueRange(20, 20, 180)

    let value = 0
    let min = 0
    let max = 0
    for (let employee of employees) {
      value += employee.iq.value * employee.moodIqModifier
      min += employee.iq.min
      max += employee.iq.max
    }

    return new ValueRange(value, 0, max)
  }

  toJSON() {
    return serialize(this)
  }
}
